import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { FiChevronDown } from 'react-icons/fi';
import toast from 'react-hot-toast';
import { getVendorOrders, updateOrderStatus } from '../../services/api';
import VendorLayout from '../../components/layout/VendorLayout';
import './VendorOrders.css';


const STATUS_CONFIG = {
  placed:     { cls: 'badge-info',    label: 'Placed' },
  confirmed:  { cls: 'badge-primary', label: 'Confirmed' },
  processing: { cls: 'badge-warning', label: 'Processing' },
  shipped:    { cls: 'badge-accent',  label: 'Shipped' },
  delivered:  { cls: 'badge-success', label: 'Delivered' },
  cancelled:  { cls: 'badge-danger',  label: 'Cancelled' },
};

const NEXT_STATUSES = {
  placed:     ['confirmed', 'cancelled'],
  confirmed:  ['processing', 'cancelled'],
  processing: ['shipped', 'cancelled'],
  shipped:    ['delivered'],
  delivered:  [],
  cancelled:  [],
};

const FILTER_TABS = [
  { key: 'all', label: 'All' },
  { key: 'placed', label: 'Placed' },
  { key: 'confirmed', label: 'Confirmed' },
  { key: 'processing', label: 'Processing' },
  { key: 'shipped', label: 'Shipped' },
  { key: 'delivered', label: 'Delivered' },
  { key: 'cancelled', label: 'Cancelled' },
];

export default function VendorOrders() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [updating, setUpdating] = useState(null);

  const fetchOrders = useCallback(async () => {
    setLoading(true);
    try {
      const params = { limit: 100 };
      const { data } = await getVendorOrders(params);
      setOrders(data.orders || []);
    } catch (err) {
      console.error('Vendor orders error:', err?.response?.data || err.message);
      setOrders([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchOrders(); }, [fetchOrders]);

  const handleStatusChange = async (order, newStatus) => {
    setUpdating(order._id);
    try {
      await updateOrderStatus(order._id, { status: newStatus });
      setOrders((prev) =>
        prev.map((o) => o._id === order._id ? { ...o, orderStatus: newStatus } : o)
      );
      toast.success(`Order marked as ${newStatus}`);
    } catch {
      toast.error('Failed to update status');
    } finally {
      setUpdating(null);
    }
  };

  const filtered = filter === 'all' ? orders : orders.filter((o) => o.orderStatus === filter);

  return (
    <VendorLayout title="Orders">
      <div className="vorders-page">
        {/* Filter tabs */}
        <div className="vorders-tabs">
          {FILTER_TABS.map((tab) => {
            const count = tab.key === 'all' ? orders.length : orders.filter((o) => o.orderStatus === tab.key).length;
            return (
              <button
                key={tab.key}
                className={`vorders-tab ${filter === tab.key ? 'active' : ''}`}
                onClick={() => setFilter(tab.key)}
              >
                {tab.label}
                {count > 0 && <span className="vorders-tab-count">{count}</span>}
              </button>
            );
          })}
        </div>

        {loading ? (
          <div className="loading-center"><div className="spinner" /><span>Loading orders…</span></div>
        ) : filtered.length === 0 ? (
          <div className="empty-state">
            <div className="empty-state-icon">📦</div>
            <h3>{filter === 'all' ? 'No orders yet' : `No ${filter} orders`}</h3>
            <p>Orders will appear here once buyers purchase your products.</p>
          </div>
        ) : (
          <div className="table-wrapper">
            <table>
              <thead>
                <tr>
                  <th>Order</th>
                  <th>Buyer</th>
                  <th>Items</th>
                  <th>Total</th>
                  <th style={{ textAlign: 'center' }}>Status</th>
                  <th style={{ textAlign: 'center' }}>Update</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((order) => {
                  const { cls } = STATUS_CONFIG[order.orderStatus] || { cls: 'badge-info' };
                  const date = new Date(order.createdAt).toLocaleDateString('en-PK', { day: 'numeric', month: 'short', year: 'numeric' });
                  const nextOpts = NEXT_STATUSES[order.orderStatus] || [];
                  const isUpdating = updating === order._id;

                  return (
                    <tr key={order._id}>
                      <td>
                        <p className="vo-order-num">{order.orderNumber}</p>
                        <p className="vo-order-date">{date}</p>
                      </td>
                      <td className="font-medium">{order.buyer?.name || '—'}</td>
                      <td className="text-muted text-sm">
                        {(order.items || []).slice(0, 2).map((i) => `${i.name || i.product?.name || '—'}${i.qty > 1 ? ` ×${i.qty}` : ''}`).join(', ')}
                        {order.items?.length > 2 && ` +${order.items.length - 2} more`}
                      </td>
                      <td className="font-semi">Rs. {Number(order.total).toFixed(2)}</td>
                      <td style={{ textAlign: 'center' }}>
                        <span className={`badge ${cls}`}>{order.orderStatus}</span>
                      </td>
                      <td style={{ textAlign: 'center' }}>
                        {nextOpts.length > 0 ? (
                          <div className="vo-status-select-wrap">
                            <select
                              className="vo-status-select"
                              defaultValue=""
                              onChange={(e) => { if (e.target.value) handleStatusChange(order, e.target.value); e.target.value = ''; }}
                              disabled={isUpdating}
                            >
                              <option value="" disabled>Move to…</option>
                              {nextOpts.map((s) => (
                                <option key={s} value={s}>{STATUS_CONFIG[s]?.label || s}</option>
                              ))}
                            </select>
                            <FiChevronDown size={12} className="vo-select-icon" />
                          </div>
                        ) : (
                          <span className="text-muted text-sm">—</span>
                        )}
                      </td>
                      <td>
                        <Link to={`/orders/${order._id}`} className="btn btn-ghost btn-sm">View</Link>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </VendorLayout>
  );
}
